import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API, formatPrice } from '@/lib/utils';
import { useSEO } from '@/lib/useSEO';
import AffiliateButton from '@/components/AffiliateButton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/Tabs';
import { ArrowLeft, ArrowRight, Users, Baby, PawPrint, Calendar, CheckCircle, Package, Printer, ArrowClockwise } from '@phosphor-icons/react';

const EMERGENCY_OPTIONS = [
  { slug: 'algemeen-72-uur', name: 'Algemeen 72-uur' },
  { slug: 'stroomuitval', name: 'Stroomuitval' },
  { slug: 'overstroming', name: 'Overstroming' },
  { slug: 'brand', name: 'Brand' },
  { slug: 'pandemie', name: 'Pandemie' },
  { slug: 'oorlog-conflict', name: 'Oorlog & conflict' },
];

const STEPS = ['Huishouden', 'Duur', 'Scenario', 'Resultaat'];

export default function Wizard() {
  useSEO({
    title: 'Pakket-wizard — stel jouw noodpakket samen',
    description: 'Geef je huishouden en crisisscenario op en ontvang in 3 minuten een persoonlijke checklist met aanbevolen producten en affiliate-links.',
    path: '/wizard',
  });
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    people: 2,
    children: 0,
    pets: 0,
    duration_days: 3,
    emergency_types: ['algemeen-72-uur'],
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleEmergency = (slug) => {
    setForm((f) => ({
      ...f,
      emergency_types: f.emergency_types.includes(slug)
        ? f.emergency_types.filter((s) => s !== slug)
        : [...f.emergency_types, slug],
    }));
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/wizard/recommend`, form);
      setResult(data);
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setStep(0); setResult(null); };

  return (
    <div data-testid="page-wizard" className="min-h-[80vh]">
      <section className="border-b border-border bg-card">
        <div className="container py-10 lg:py-14">
          <div className="overline text-muted-foreground mb-3">Pakket-wizard</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Bouw jouw persoonlijke noodpakket.
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl">
            In 3 stappen stellen we een checklist samen op basis van jouw huishouden. Daarna tonen we aanbevolen producten met directe links.
          </p>

          {/* Step indicator */}
          <div className="mt-10 grid grid-cols-4 gap-2">
            {STEPS.map((label, i) => (
              <div
                key={label}
                data-testid={`wizard-step-${i}`}
                className={`h-1.5 rounded-full transition-colors ${i <= step ? 'bg-accent' : 'bg-border'}`}
              />
            ))}
          </div>
          <div className="mt-3 flex justify-between text-xs overline text-muted-foreground">
            {STEPS.map((l, i) => (
              <div key={l} className={i === step ? 'text-foreground font-bold' : ''}>{i + 1}. {l}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="container py-12 lg:py-16 max-w-4xl">
        {step === 0 && (
          <div className="space-y-8 animate-fade-up" data-testid="wizard-step-household">
            <h2 className="font-display font-black text-2xl lg:text-3xl">Wie hoort bij jouw huishouden?</h2>

            <Counter
              icon={Users}
              label="Volwassenen + tieners"
              hint="Personen van 12 jaar en ouder."
              value={form.people}
              onChange={(v) => update('people', Math.max(1, v))}
              min={1}
              testId="counter-people"
            />
            <Counter
              icon={Baby}
              label="Jonge kinderen"
              hint="Onder 12 jaar. Extra luiers/voeding in de checklist."
              value={form.children}
              onChange={(v) => update('children', Math.max(0, v))}
              testId="counter-children"
            />
            <Counter
              icon={PawPrint}
              label="Huisdieren"
              hint="Honden, katten — extra voer en water."
              value={form.pets}
              onChange={(v) => update('pets', Math.max(0, v))}
              testId="counter-pets"
            />

            <div className="pt-6 flex justify-end">
              <button
                onClick={() => setStep(1)}
                data-testid="wizard-next-0"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-primary-foreground px-6 py-3 rounded-sm font-bold transition-colors"
              >
                Volgende <ArrowRight weight="bold" size={18} />
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-8 animate-fade-up" data-testid="wizard-step-duration">
            <h2 className="font-display font-black text-2xl lg:text-3xl">Voor hoeveel dagen wil je voorbereid zijn?</h2>
            <p className="text-muted-foreground -mt-4">De overheid adviseert minimaal 3 dagen. Serieuze preppers houden 14 dagen of meer aan.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[3, 7, 14, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => update('duration_days', d)}
                  data-testid={`duration-${d}`}
                  className={`border rounded-sm py-6 px-5 text-left transition-all ${form.duration_days === d ? 'border-accent bg-accent/10' : 'border-border bg-card hover:border-primary'}`}
                >
                  <div className="flex items-center gap-2 overline text-muted-foreground mb-2">
                    <Calendar size={14} /> Dagen
                  </div>
                  <div className="font-display font-black text-3xl">{d}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {d === 3 ? 'Overheidsminimum' : d === 7 ? 'Aangeraden' : d === 14 ? 'Comfortabel' : 'Serieus voorbereid'}
                  </div>
                </button>
              ))}
            </div>

            <div className="pt-6 flex justify-between">
              <button onClick={() => setStep(0)} data-testid="wizard-prev-1" className="inline-flex items-center gap-2 text-foreground px-4 py-3 font-semibold">
                <ArrowLeft weight="bold" size={18} /> Terug
              </button>
              <button onClick={() => setStep(2)} data-testid="wizard-next-1" className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-primary-foreground px-6 py-3 rounded-sm font-bold transition-colors">
                Volgende <ArrowRight weight="bold" size={18} />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8 animate-fade-up" data-testid="wizard-step-emergencies">
            <h2 className="font-display font-black text-2xl lg:text-3xl">Welke scenario's zijn relevant voor jou?</h2>
            <p className="text-muted-foreground -mt-4">Kies één of meerdere. We voegen specifieke items toe per scenario.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {EMERGENCY_OPTIONS.map((o) => {
                const active = form.emergency_types.includes(o.slug);
                return (
                  <button
                    key={o.slug}
                    onClick={() => toggleEmergency(o.slug)}
                    data-testid={`emergency-option-${o.slug}`}
                    className={`border rounded-sm py-5 px-5 text-left transition-all relative ${active ? 'border-accent bg-accent/10' : 'border-border bg-card hover:border-primary'}`}
                  >
                    {active && (
                      <CheckCircle weight="fill" size={18} className="absolute top-3 right-3 text-accent" />
                    )}
                    <div className="font-display font-bold text-lg leading-tight">{o.name}</div>
                  </button>
                );
              })}
            </div>

            <div className="pt-6 flex justify-between">
              <button onClick={() => setStep(1)} data-testid="wizard-prev-2" className="inline-flex items-center gap-2 text-foreground px-4 py-3 font-semibold">
                <ArrowLeft weight="bold" size={18} /> Terug
              </button>
              <button
                onClick={submit}
                disabled={loading || form.emergency_types.length === 0}
                data-testid="wizard-submit"
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-accent-foreground px-6 py-3 rounded-sm font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Bezig...' : 'Genereer mijn pakket'} <ArrowRight weight="bold" size={18} />
              </button>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-10 animate-fade-up" data-testid="wizard-step-result">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="overline text-muted-foreground mb-2">Jouw persoonlijke pakket</div>
                <h2 className="font-display font-black text-3xl lg:text-4xl leading-tight">
                  {result.summary.items_total} essentiële items voor {result.summary.people} persoon{result.summary.people > 1 ? 'en' : ''}, {result.summary.duration_days} dagen.
                </h2>
              </div>
              <div className="flex gap-2">
                <button onClick={() => window.print()} data-testid="wizard-print" className="inline-flex items-center gap-2 border border-border bg-card hover:bg-muted px-4 py-2.5 rounded-sm font-semibold text-sm">
                  <Printer weight="bold" size={16} /> Print
                </button>
                <button onClick={reset} data-testid="wizard-restart" className="inline-flex items-center gap-2 border border-border bg-card hover:bg-muted px-4 py-2.5 rounded-sm font-semibold text-sm">
                  <ArrowClockwise weight="bold" size={16} /> Opnieuw
                </button>
              </div>
            </div>

            {/* Summary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Drinkwater" value={`${result.summary.water_liters_total} L`} />
              <Kpi label="Maaltijden" value={`${result.summary.meals_total}×`} />
              <Kpi label="Huisdieren" value={result.summary.pets} />
              <Kpi label="Items totaal" value={result.summary.items_total} />
            </div>

            {/* Checklist */}
            <Tabs defaultValue="all" className="w-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-black text-xl flex items-center gap-2">
                  <Package weight="duotone" size={22} /> Jouw checklist
                </h3>
                <TabsList>
                  <TabsTrigger value="all" data-testid="tab-all">Alles</TabsTrigger>
                  <TabsTrigger value="water-voedsel" data-testid="tab-water-voedsel">Water & Voedsel</TabsTrigger>
                  <TabsTrigger value="ehbo" data-testid="tab-ehbo">EHBO</TabsTrigger>
                  <TabsTrigger value="verlichting" data-testid="tab-verlichting">Verlichting</TabsTrigger>
                  <TabsTrigger value="communicatie" data-testid="tab-communicatie">Communicatie</TabsTrigger>
                  <TabsTrigger value="warmte" data-testid="tab-warmte">Warmte</TabsTrigger>
                  <TabsTrigger value="gereedschap" data-testid="tab-gereedschap">Gereedschap</TabsTrigger>
                  <TabsTrigger value="hygiene" data-testid="tab-hygiene">Hygiëne</TabsTrigger>
                  <TabsTrigger value="documenten" data-testid="tab-documenten">Documenten</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="all" className="mt-0">
                <div className="grid md:grid-cols-2 gap-3">
                  {(result.checklist || []).map((item, i) => (
                    <ChecklistItem key={i} item={item} i={i} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="water-voedsel" className="mt-0">
                <div className="grid md:grid-cols-2 gap-3">
                  {(result.checklist || []).filter(i => i.category === 'water-voedsel').map((item, i) => (
                    <ChecklistItem key={i} item={item} i={i} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="ehbo" className="mt-0">
                <div className="grid md:grid-cols-2 gap-3">
                  {(result.checklist || []).filter(i => i.category === 'ehbo').map((item, i) => (
                    <ChecklistItem key={i} item={item} i={i} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="verlichting" className="mt-0">
                <div className="grid md:grid-cols-2 gap-3">
                  {(result.checklist || []).filter(i => i.category === 'verlichting').map((item, i) => (
                    <ChecklistItem key={i} item={item} i={i} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="communicatie" className="mt-0">
                <div className="grid md:grid-cols-2 gap-3">
                  {(result.checklist || []).filter(i => i.category === 'communicatie').map((item, i) => (
                    <ChecklistItem key={i} item={item} i={i} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="warmte" className="mt-0">
                <div className="grid md:grid-cols-2 gap-3">
                  {(result.checklist || []).filter(i => i.category === 'warmte').map((item, i) => (
                    <ChecklistItem key={i} item={item} i={i} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="gereedschap" className="mt-0">
                <div className="grid md:grid-cols-2 gap-3">
                  {(result.checklist || []).filter(i => i.category === 'gereedschap').map((item, i) => (
                    <ChecklistItem key={i} item={item} i={i} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="hygiene" className="mt-0">
                <div className="grid md:grid-cols-2 gap-3">
                  {(result.checklist || []).filter(i => i.category === 'hygiene').map((item, i) => (
                    <ChecklistItem key={i} item={item} i={i} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="documenten" className="mt-0">
                <div className="grid md:grid-cols-2 gap-3">
                  {(result.checklist || []).filter(i => i.category === 'documenten').map((item, i) => (
                    <ChecklistItem key={i} item={item} i={i} />
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            {/* Recommended products */}
            {result.recommended_products.length > 0 && (
              <div className="bg-primary text-primary-foreground rounded-sm p-8 lg:p-12">
                <div className="flex items-center gap-2 overline text-primary-foreground/70 mb-3">
                  <span className="w-8 h-0.5 bg-accent" /> Direct bestellen
                </div>
                <h3 className="font-display font-black text-2xl lg:text-3xl mb-8">
                  Aanbevolen producten voor jouw pakket
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {(result.recommended_products || []).slice(0, 8).map((p) => (
                    <div key={p.slug} className="bg-background text-foreground rounded-sm p-4 flex gap-4" data-testid={`rec-product-${p.slug}`}>
                      <img src={p.image} alt={p.name} className="w-20 h-20 object-cover rounded-sm flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs overline text-muted-foreground">{p.brand}</div>
                        <Link to={`/product/${p.slug}`} className="font-display font-bold leading-tight hover:text-primary block truncate">
                          {p.name}
                        </Link>
                        <div className="text-sm font-black mt-1">{formatPrice(p.price)}</div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {(p.affiliates || []).slice(0, 3).map((a) => (
                            <AffiliateButton key={a.shop} shop={a.shop} url={a.url} size="sm" testId={`rec-aff-${p.slug}-${a.shop}`} />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function Counter({ icon: Icon, label, hint, value, onChange, min = 0, testId }) {
  return (
    <div className="border border-border bg-card rounded-sm p-6 flex items-center gap-5">
      <div className="w-12 h-12 bg-primary/10 text-primary rounded-sm flex items-center justify-center flex-shrink-0">
        <Icon weight="duotone" size={26} />
      </div>
      <div className="flex-1">
        <div className="font-display font-bold text-lg leading-tight">{label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(value - 1)}
          disabled={value <= min}
          data-testid={`${testId}-minus`}
          className="w-10 h-10 border border-border hover:border-primary rounded-sm font-black text-xl disabled:opacity-30 disabled:cursor-not-allowed"
        >−</button>
        <div data-testid={`${testId}-value`} className="w-12 text-center font-display font-black text-2xl">{value}</div>
        <button
          onClick={() => onChange(value + 1)}
          data-testid={`${testId}-plus`}
          className="w-10 h-10 border border-border hover:border-primary rounded-sm font-black text-xl"
        >+</button>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-card border border-border rounded-sm p-5">
      <div className="overline text-muted-foreground mb-1">{label}</div>
      <div className="font-display font-black text-3xl">{value}</div>
    </div>
  );
}

function ChecklistItem({ item, i }) {
  return (
    <div className="border border-border bg-card rounded-sm p-5 flex flex-col" data-testid={`checklist-item-${i}`}>
      <div className="flex items-start gap-3">
        <div className="w-5 h-5 border-2 border-primary rounded-sm flex-shrink-0 mt-1" />
        <div className="flex-1">
          <div className="font-bold leading-tight">{item.label}</div>
          <div className="text-xs text-muted-foreground mt-1">{item.quantity}</div>
        </div>
      </div>
      {item.product && (
        <div className="mt-4 pt-4 border-t border-dashed border-border flex items-center justify-between gap-3">
          <Link to={`/product/${item.product.slug}`} className="flex items-center gap-3 flex-1 min-w-0 group">
            <img src={item.product.image} alt="" className="w-10 h-10 object-cover rounded-sm flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">Aanbeveling</div>
              <div className="text-sm font-semibold truncate group-hover:text-primary">{item.product.name}</div>
            </div>
          </Link>
          <div className="font-display font-black text-sm whitespace-nowrap">{formatPrice(item.product.price)}</div>
        </div>
      )}
    </div>
  );
}
